import { HTMLProps, ReactNode } from "react";
import CorePaper, { CorePaperProps } from "../paper";
import { Typography, TypographyProps } from "@mui/material";
import CoreBackButton from "../button/BackButton";
import i18nPackage from "../../i18n/i18n";
import styles from "./TitleCard.module.css";

export type CoreTitleCardProps = {
	title?: ReactNode;
	description?: ReactNode;
	rightContent?: ReactNode;
	topContent?: ReactNode;
	/**Name of the page the back button navigates to, e.g. "Assessments". When paired with `onBack`, renders the back button (icon only) beside the title instead of via `topContent`. */
	backButtonPageName?: string;
	onBack?: () => void;
	slotProps?: { text?: HTMLProps<HTMLDivElement>; title?: TypographyProps; description?: TypographyProps };
} & Omit<CorePaperProps, "children" | "title">;

export function CoreTitleCardHeader({ sx, children, ...typographyProps }: TypographyProps) {
	return (
		<Typography {...typographyProps} sx={{ fontSize: 20, fontWeight: 600, textAlign: "start", ...sx }}>
			{children}
		</Typography>
	);
}

export function CoreTitleCardDescription({ children, className, ...typographyProps }: TypographyProps) {
	return (
		<Typography {...typographyProps} className={`${styles.description} ${className || ""}`}>
			{children}
		</Typography>
	);
}

export default function CoreTitleCard({
	title,
	description,
	rightContent,
	topContent,
	backButtonPageName,
	onBack,
	className,
	slotProps,
	...paperProps
}: CoreTitleCardProps) {
	const showBackButtonBesideTitle = Boolean(backButtonPageName && onBack);
	return (
		<CorePaper {...paperProps} className={`${styles.header} ${className || ""}`} component="header">
			<div {...slotProps?.text} className={`${styles.headerInfo} ${slotProps?.text?.className || ""}`}>
				{!showBackButtonBesideTitle && topContent}
				{title &&
					(showBackButtonBesideTitle ? (
						<div className={styles.titleRow}>
							<CoreBackButton
								onBack={onBack as () => void}
								iconOnly
								ariaLabel={
									i18nPackage.t("coreComponents.titleCard.backButtonAriaLabel", {
										pageName: backButtonPageName,
									})
								}
							/>
							<CoreTitleCardHeader variant="h1" {...slotProps?.title}>
								{title}
							</CoreTitleCardHeader>
						</div>
					) : (
						<CoreTitleCardHeader variant="h1" {...slotProps?.title}>
							{title}
						</CoreTitleCardHeader>
					))}
				{description && <CoreTitleCardDescription {...slotProps?.description}>{description}</CoreTitleCardDescription>}
			</div>
			{rightContent}
		</CorePaper>
	);
}
